import React, { useState, useMemo } from 'react';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { Card, CardHeader } from '../shared';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../utils/formatters';
import { Debt, DEBT_PAYOFF_MAX_MONTHS, simulateDebtPayoff } from '../../utils/calculators';

interface DebtPayoffCalculatorProps {
  variant?: 'card' | 'page';
}

const DEFAULT_DEBTS: Debt[] = [
  { name: 'Credit Card', balance: 5000, apr: 22, minPayment: 150 },
  { name: 'Car Loan', balance: 15000, apr: 6, minPayment: 350 },
];

export const DebtPayoffCalculator: React.FC<DebtPayoffCalculatorProps> = ({ variant = 'card' }) => {
  const { darkMode } = useTheme();
  const [debts, setDebts] = useState<Debt[]>(DEFAULT_DEBTS);
  const [extraPayment, setExtraPayment] = useState(200);
  const [strategy, setStrategy] = useState<'snowball' | 'avalanche'>('avalanche');

  const inputClass = `w-full px-2 py-1.5 rounded-lg text-sm ${
    darkMode
      ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
      : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
  }`;

  const labelClass = `text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;

  const updateDebt = (idx: number, field: keyof Debt, value: string | number) => {
    setDebts(prev => prev.map((d, i) => i === idx ? { ...d, [field]: typeof value === 'string' ? value : +value } : d));
  };

  const addDebt = () => setDebts(prev => {
    const names = new Set(prev.map(d => d.name));
    let n = prev.length + 1;
    while (names.has(`Debt ${n}`)) n++;
    return [...prev, { name: `Debt ${n}`, balance: 1000, apr: 15, minPayment: 50 }];
  });
  const removeDebt = (idx: number) => setDebts(prev => prev.filter((_, i) => i !== idx));

  const result = useMemo(() => simulateDebtPayoff(debts.filter(d => d.balance > 0), extraPayment, strategy), [debts, extraPayment, strategy]);
  const minOnly = useMemo(() => simulateDebtPayoff(debts.filter(d => d.balance > 0), 0, strategy), [debts, strategy]);

  const interestSaved = minOnly.totalInterest - result.totalInterest;
  const debtFreeDate = new Date();
  debtFreeDate.setMonth(debtFreeDate.getMonth() + result.months);

  const pillClass = (active: boolean) => `flex-1 px-3 py-2 rounded-full text-xs font-semibold transition-all text-center ${
    active
      ? (darkMode ? 'bg-violet-500/30 text-violet-300' : 'bg-violet-100 text-violet-700')
      : (darkMode ? 'bg-white/5 text-slate-500 hover:bg-white/10' : 'bg-slate-50 text-slate-400 hover:bg-slate-100')
  }`;

  return (
    <Card variant={variant}>
      <CardHeader
        title="Debt Payoff Planner"
        icon={<CreditCard size={20} />}
        variant={variant}
      />
      <div className="space-y-4">
        {/* Strategy toggle */}
        <div className="flex gap-2">
          <button onClick={() => setStrategy('avalanche')} className={pillClass(strategy === 'avalanche')}>
            Avalanche (Highest APR)
          </button>
          <button onClick={() => setStrategy('snowball')} className={pillClass(strategy === 'snowball')}>
            Snowball (Lowest Balance)
          </button>
        </div>

        {/* Debt rows */}
        <div className="space-y-2">
          {debts.map((debt, idx) => (
            <div key={idx} className={`rounded-lg p-3 group relative ${darkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className={labelClass}>Name</label>
                  <input value={debt.name} onChange={e => updateDebt(idx, 'name', e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Balance</label>
                  <input type="number" value={debt.balance} onChange={e => updateDebt(idx, 'balance', +e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>APR %</label>
                  <input type="number" value={debt.apr} step={0.5} onChange={e => updateDebt(idx, 'apr', +e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Min Pmt</label>
                  <input type="number" value={debt.minPayment} onChange={e => updateDebt(idx, 'minPayment', +e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
                </div>
              </div>
              {debts.length > 1 && (
                <button
                  onClick={() => removeDebt(idx)}
                  aria-label={`Remove ${debt.name || 'debt'}`}
                  title="Remove"
                  className={`absolute top-1 right-1 p-1.5 rounded transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-visible:opacity-100 ${
                    darkMode ? 'text-slate-500 hover:text-red-400' : 'text-slate-400 hover:text-red-500'
                  }`}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
          <button onClick={addDebt} className={`flex items-center gap-1 text-xs font-medium ${darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}>
            <Plus size={14} /> Add Debt
          </button>
        </div>

        {/* Extra payment */}
        <div>
          <label className={labelClass}>Extra Monthly Payment</label>
          <input type="number" value={extraPayment} onChange={e => setExtraPayment(+e.target.value)} onFocus={e => e.target.select()} className={`${inputClass} w-32`} />
        </div>

        {/* Results */}
        <div className={`rounded-xl p-4 space-y-3 ${darkMode ? 'bg-white/5' : 'bg-violet-50/50'}`}>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Debt-Free In</p>
              <p className={`text-lg font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                {result.months < DEBT_PAYOFF_MAX_MONTHS ? `${Math.floor(result.months / 12)}y ${result.months % 12}m` : 'N/A'}
              </p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Debt-Free Date</p>
              <p className={`text-lg font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                {result.months < DEBT_PAYOFF_MAX_MONTHS ? debtFreeDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Total Interest</p>
              <p className={`text-sm font-semibold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                {formatCurrency(result.totalInterest)}
              </p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Interest Saved</p>
              <p className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {formatCurrency(interestSaved)}
              </p>
            </div>
          </div>

          {/* Payoff order */}
          {result.payoffOrder.length > 0 && (
            <div>
              <p className={`text-xs font-medium mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Payoff Order</p>
              <div className="space-y-1">
                {result.payoffOrder.map((name, i) => (
                  <div key={i} className={`flex items-center gap-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      darkMode ? 'bg-violet-500/30 text-violet-300' : 'bg-violet-100 text-violet-700'
                    }`}>{i + 1}</span>
                    {name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Estimates only. Not financial advice. Actual results will vary.
        </p>
      </div>
    </Card>
  );
};
